import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { users as usersApi, locations as locationsApi } from "../api/endpoints";
import {
  getBrowserTimezone,
  getBrowserPosition,
  reverseGeocode,
  ipGeolocation,
  countryNameFromCode,
} from "../utils/locationUtils";
import type { ReverseGeoResult } from "../utils/locationUtils";
import { useAuth } from "./AuthContext";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UserSettingsValue {
  /** IANA timezone string (e.g. "America/New_York"). Defaults to "UTC". */
  timezone: string;
  /** ISO 3166-1 alpha-2 country code from user's stored location (e.g. "US"). */
  countryCode: string;
  /** The location_id stored in user_settings, if any. */
  locationId: number | null;
  /** True once the first settings load has completed (regardless of success). */
  settingsLoaded: boolean;
  /**
   * True when neither GPS nor IP geolocation could determine the user's country
   * and no country is currently saved. The UI should show a manual picker.
   */
  locationPromptNeeded: boolean;
  /**
   * Called from the manual country picker — creates a location record, saves
   * it to user_settings, and clears the prompt flag.
   */
  saveCountry: (countryCode: string) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const UserSettingsContext = createContext<UserSettingsValue | undefined>(
  undefined
);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function UserSettingsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { isAuthenticated } = useAuth();

  const [timezone, setTimezone] = useState("UTC");
  const [countryCode, setCountryCode] = useState("");
  const [locationId, setLocationId] = useState<number | null>(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [locationPromptNeeded, setLocationPromptNeeded] = useState(false);

  // Guard against running the sync more than once per auth session.
  const syncedRef = useRef(false);

  const syncSettings = useCallback(async () => {
    try {
      const detectedTz = getBrowserTimezone();

      // 1. Load existing settings (backend creates a default row if missing).
      const existing = await usersApi.getSettings();

      let newTimezone = existing?.timezone ?? "UTC";
      let newLocationId: number | null = existing?.location_id ?? null;
      let existingCountryCode = "";
      let needsUpdate = false;

      // 2. Check timezone — update only if it actually changed.
      if (detectedTz && detectedTz !== "UTC" && detectedTz !== newTimezone) {
        newTimezone = detectedTz;
        needsUpdate = true;
      }

      // 3. If user already has a saved location, fetch it to get country code.
      if (existing?.location_id) {
        try {
          const existingLoc = await locationsApi.get(existing.location_id);
          existingCountryCode = existingLoc?.country_code ?? "";
          setCountryCode(existingCountryCode);
          setLocationId(existing.location_id);
        } catch {
          /* ignore — use empty country */
        }
      }

      // 4a. Try GPS + reverse geocode first.
      let detectedGeo: (ReverseGeoResult & { latitude?: number; longitude?: number }) | null = null;
      try {
        const pos = await getBrowserPosition();
        const geo = await reverseGeocode(pos.latitude, pos.longitude);
        detectedGeo = { ...geo, latitude: pos.latitude, longitude: pos.longitude };
      } catch {
        // GPS denied or timed out — fall through to IP.
      }

      // 4b. GPS failed → always try IP, regardless of whether a saved country exists.
      //     This ensures location updates if the user has moved to a new country.
      if (!detectedGeo) {
        try {
          const geo = await ipGeolocation();
          detectedGeo = geo;
        } catch {
          // IP also failed — will show manual picker below if no country exists.
        }
      }

      // 4c. Apply the detected location if we got one.
      if (detectedGeo?.countryCode) {
        const detectedCountry = detectedGeo.countryCode;
        if (detectedCountry !== existingCountryCode) {
          // Country changed (or not set yet) → create a fresh location record.
          const loc = await locationsApi.create({
            address_line1: detectedGeo.city ?? countryNameFromCode(detectedCountry),
            city: detectedGeo.city ?? null,
            state_province: detectedGeo.stateProvince ?? null,
            country_code: detectedCountry,
            latitude: detectedGeo.latitude ?? null,
            longitude: detectedGeo.longitude ?? null,
          });
          if (loc?.id) {
            newLocationId = loc.id;
            setCountryCode(detectedCountry);
            setLocationId(loc.id);
            needsUpdate = true;
          }
        } else {
          // Same country — no new record needed; just ensure local state is fresh.
          setCountryCode(detectedCountry);
        }
      } else if (!existingCountryCode) {
        // Neither GPS nor IP returned a country and the user has nothing saved →
        // show the manual country picker.
        setLocationPromptNeeded(true);
      }

      // 5. Persist settings to backend only if something changed.
      if (needsUpdate) {
        await usersApi.upsertSettings({
          location_id: newLocationId,
          timezone: newTimezone,
        });
      }

      setTimezone(newTimezone);
      setSettingsLoaded(true);
    } catch {
      // Any unexpected failure — mark as loaded so the UI doesn't block.
      setSettingsLoaded(true);
    }
  }, []);

  /**
   * Called when the user manually selects their country from the prompt.
   * Creates a minimal location record and persists it to user_settings.
   */
  const saveCountry = useCallback(
    async (code: string) => {
      try {
        const loc = await locationsApi.create({
          address_line1: countryNameFromCode(code),
          country_code: code,
        });
        if (loc?.id) {
          await usersApi.upsertSettings({
            location_id: loc.id,
            timezone,
          });
          setCountryCode(code);
          setLocationId(loc.id);
          setLocationPromptNeeded(false);
        }
      } catch {
        /* ignore — user can try again later */
      }
    },
    [timezone]
  );

  // Run the sync once when the user is authenticated.
  useEffect(() => {
    if (isAuthenticated && !syncedRef.current) {
      syncedRef.current = true;
      syncSettings();
    }
    if (!isAuthenticated) {
      // Reset on logout.
      syncedRef.current = false;
      setTimezone("UTC");
      setCountryCode("");
      setLocationId(null);
      setSettingsLoaded(false);
      setLocationPromptNeeded(false);
    }
  }, [isAuthenticated, syncSettings]);

  const value = useMemo<UserSettingsValue>(
    () => ({
      timezone,
      countryCode,
      locationId,
      settingsLoaded,
      locationPromptNeeded,
      saveCountry,
    }),
    [timezone, countryCode, locationId, settingsLoaded, locationPromptNeeded, saveCountry]
  );

  return (
    <UserSettingsContext.Provider value={value}>
      {children}
    </UserSettingsContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useUserSettings(): UserSettingsValue {
  const ctx = useContext(UserSettingsContext);
  if (!ctx)
    throw new Error(
      "useUserSettings must be used within <UserSettingsProvider>"
    );
  return ctx;
}
